/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { IrisModuleFilterService } from '../../services/module-filter.service';
import { Observable, combineLatest } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { IrisConfirmationService } from '@iris/common/services/confirmation.service';
import { MatDialog } from '@angular/material/dialog';
import { IrisModuleSaveFilterModalComponent } from '../module-save-filter-modal/module-save-filter-modal.component';
import { IrisFilterI, IrisModuleFilterI } from '../../models/IrisModuleFilter';
import { IrisTranslatePipe } from '@iris/common/modules/pipes-common/pipes/translate/translate.pipe';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import { IrisModuleFilterSandbox } from '@iris/common/modules/module-filter/store/module-filter.sandbox';
import { FilterValidatorFnI } from '@iris/common/modules/module-filter/models/interfaces/filter-validator-fn.interface';
import { SubscriptionMixin } from '@iris/common/mixins/subscription.mixin';
import { map } from 'rxjs/operators';
import { IrisActiveFilterService } from '@iris/modules/module-filters-v2/active-filter';
import { IrisModuleFilterOverlayService } from '../../services/module-filter-overlay.service';
import { IRIS_ACTIVE_SIDEBAR_FILTER } from '@iris/modules/module-filters-v2/tokens';

@Component({
  selector: 'iris-module-filter-buttons',
  templateUrl: './module-filter-buttons.component.html',
  styleUrls: ['./module-filter-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IrisModuleFilterButtonsComponent extends SubscriptionMixin() implements OnInit {
  private readonly moduleFilterService = inject(IrisModuleFilterService);
  private readonly translate = inject(TranslateService);
  private readonly confirmationService = inject(IrisConfirmationService);
  private readonly dialog = inject(MatDialog);
  private readonly irisTranslatePipe = inject(IrisTranslatePipe);
  private readonly moduleFilterSandbox = inject(IrisModuleFilterSandbox);
  // keep this filter to make it work
  private readonly filterOverlayService = inject(IrisModuleFilterOverlayService);
  private readonly activeFilterService = inject<IrisActiveFilterService>(IRIS_ACTIVE_SIDEBAR_FILTER, { optional: true });

  @Input() showTitle = false;

  userFilters$ = this.moduleFilterSandbox.userFilters$;
  pinnedUserFilters$: Observable<IrisModuleFilterI[]> = this.moduleFilterSandbox.pinnedUserFilters$;
  recentFilters$ = this.moduleFilterSandbox.recentFilters$;

  shouldShowNoFilters$ = combineLatest([
    this.userFilters$,
    this.recentFilters$,
  ]).pipe(map(([userFilters, pinnedFilters]) => !userFilters.length && !pinnedFilters.length));

  shouldShowRecentlyUsed$ = this.recentFilters$.pipe(map((filters) => !!filters.length));
  shouldShowSaved$ = this.userFilters$.pipe(map((filters) => !!filters.length));

  @Input() showPinnedFilters = true;
  @Input() showFilterMenu = true;

  _formTemplate;
  @Input() set formTemplate(formTemplate) {
    this._formTemplate = formTemplate;
    if (formTemplate) {
      this.moduleFilterService.setFormTemplateRef(formTemplate);
    }
  }

  get formTemplate() {
    return this._formTemplate;
  }

  _propertiesMeta;
  @Input() set propertiesMeta(propertiesMeta) {
    this._propertiesMeta = propertiesMeta;
    // INFO
    // workaround with Object.freeze needed to avoid error from NgRx immutability runtime checks
    // BUT (!) initial problem is not solved with this workaround
    // the initial problem is that we are trying to save complex entities (with injected services, etc) into NgRx store
    // for example, some filter meta object with injected UserService with injected HttpClient with Injector with >100 services inside
    // WE SHOULD NOT save such entities in store, only some plane objects should be stored in store
    const frozenObject = Object.freeze(propertiesMeta);
    this.moduleFilterSandbox.setPropertiesMeta(frozenObject);

  }

  get propertiesMeta() {
    return this._propertiesMeta;
  }

  _filter;
  @Input() set filter(filter) {
    if (isEqual(this._filter, filter)) {
      return;
    }
    this._filter = filter;
    this.moduleFilterSandbox.setFilter(filter);
  }

  get filter() {
    return this._filter;
  }

  _moduleName;
  @Input() set moduleName(moduleName) {
    this._moduleName = moduleName;
    if (moduleName) {
      this.moduleFilterSandbox.setModuleName(moduleName);
    }
  }

  get moduleName() {
    return this._moduleName;
  }

  @Input() resetButton = false;

  @Output() filterChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() filterApplied: EventEmitter<IrisFilterI> = new EventEmitter<IrisFilterI>();

  hoverFilterId: number;
  filterDirty: any;
  defaultFilter: any;

  ngOnInit() {
    this.defaultFilter = this.filter;
    this.moduleFilterSandbox.fetchUsersFilters();

    this.subscribe(
      this.moduleFilterSandbox.filterDirty$,
      (filterDirty) => this.filterDirty = filterDirty,
    );

    this.subscribe(
      this.moduleFilterSandbox.watchForDifferentFilter$(this.filter),
      (filter: unknown) => this.filterChange.emit(filter),
    );

    this.subscribe(
      this.moduleFilterSandbox.filterApplied$,
      (mergedFilter) => {
        this.filterApplied.emit({
          entity: mergedFilter,
          filter: this.moduleFilterService.extractFilter(this.propertiesMeta, mergedFilter),
          params: this.moduleFilterService.extractParams(this.propertiesMeta, mergedFilter),
        });
      },
    );

    this.subscribe(
      this.moduleFilterSandbox.defaultFilterApplied$,
      floatingFilter => {
        this.filter = this.moduleFilterService.mergeFilters(this.defaultFilter, floatingFilter ?? {});

        this.filterApplied.emit({
          entity: {},
          filter: !isEmpty(floatingFilter)
            ? this.moduleFilterService.extractFilter(this.propertiesMeta, floatingFilter)
            : null,
        });
      },
    );
  }

  toggleFilterVisibility() {
    this.moduleFilterSandbox.toggleFilterVisibility(null);
  }

  toggleMenuVisibility() {
    this.moduleFilterSandbox.toggleFilterMenuVisibility(null);
  }

  applyCustomFilter(filter: any) {
    this.moduleFilterSandbox.applyFilter(filter);
    this.activeFilterService?.updateValues({ ...filter });
  }

  setFilterValidationFn(fn: FilterValidatorFnI) {
    this.moduleFilterSandbox.setValidatorFn(fn);
  }

  resetFilterToDefault() {
    this.moduleFilterSandbox.applyDefaultFilter();
    this.activeFilterService?.resetValues();
  }

  updateUserFilter(filter: IrisModuleFilterI) {
    this.confirmationService.show(
      this.translate
        .instant('message.mf.RewriteFilterConfirm')
        .replace(
          '{0}',
          this.irisTranslatePipe.transform(
            filter.name,
            filter.translations.name,
          ),
        ),
    )
      .then(() => {
        this.moduleFilterSandbox.saveFilter({
          ...filter,
          filter: this.filterDirty || this.filter || {},
        });
      }, () => void 0);
  }

  editUserFilter(filter) {
    this.subscribeOnce(
      this.dialog.open(IrisModuleSaveFilterModalComponent, {
        data: {
          userFilter: filter,
        },
        maxWidth: '500px',
      }).componentInstance.result$,
      (moduleFilter) => {
        this.moduleFilterSandbox.saveFilter({
          ...moduleFilter,
          ...moduleFilter.isPublic && { isPinned: moduleFilter.isPinned || moduleFilter.isPinnedByDefault },
        });
      },
    );
  }

  removeUserFilter(filter) {
    this.confirmationService.show(
      this.translate.instant('message.DeleteItemConfirm'),
    )
      .then(
        () => this.moduleFilterSandbox.removeFilter(filter.id),
        () => null,
      );
  }

  pinCustomFilter(filter: IrisModuleFilterI, isPin: boolean): void {
    const filterId = filter?.id;
    if (filterId) {
      this.moduleFilterSandbox.pinFilter(filterId, isPin);
    }
  }
}
